import clsx from 'clsx';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

import styles from './base-button.module.scss';

export type ButtonSizes = 'small' | 'medium' | 'large';
export type ButtonColorScheme = 'primary' | 'secondary' | 'tertiary';
export type ButtonVariant = 'filled' | 'outlined' | 'ghost';

type ButtonTooltip =
	| string
	| {
			content: string;
			place?: 'top' | 'right' | 'bottom' | 'left';
	  };

type BaseProps = {
	size?: ButtonSizes;
	variant?: ButtonVariant;
	colorScheme?: ButtonColorScheme;
	fullWidth?: boolean;
	loading?: boolean;
	tooltip?: ButtonTooltip;
	squared?: boolean;
};

type ButtonAsButton = Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
	as: 'button';
} & BaseProps;

type ButtonAsLink = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
	as: 'a';
} & BaseProps;

export type ButtonBaseProps = ButtonAsButton | ButtonAsLink;

// TODO: Replace other buttons with this one
const BaseButton: ReactComponent<ButtonBaseProps> = ({
	className,
	children,
	size = 'medium',
	variant = 'filled',
	colorScheme = 'primary',
	fullWidth = false,
	...rest
}) => {
	const classes = clsx(
		className,
		styles.button,
		colorScheme && styles[colorScheme],
		size && styles[size],
		variant && styles[variant],
		fullWidth && 'fullWidth'
	);

	if (rest.as === 'a') {
		return (
			<a className={classes} {...rest}>
				{children}
			</a>
		);
	}

	return (
		<button className={classes} {...rest}>
			{children}
		</button>
	);
};

export default BaseButton;
